<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import {
	Condition,
	ConditionType,
	RuleDimension,
	TimeRangeValue,
	MULTI_VALUE_CONDITION_TYPES,
	isDraftCondition,
	HierarchySelection,
	emptyHierarchySelection,
} from "@/modules/rule_engine/ruleEngineClasses";
import { parseCondition } from "@/modules/rule_engine/parser";
import { describeCondition } from "@/modules/rule_engine/describe";
import { useRuleEnginedStore } from "@/modules/rule_engine/store";
import RuleSegments from "@/modules/rule_engine/components/RuleSegments.vue";
import DimensionPicker from "@/modules/rule_engine/components/DimensionPicker.vue";
import ConfigureConditionInSelection from "./ConfigureConditionInSelection.vue";
import ConfigureConditionComparisonSelection from "./ConfigureConditionComparisonSelection.vue";
import ConfigureConditionBetweenSelection from "./ConfigureConditionBetweenSelection.vue";
import ConfigureABTestConditionSelection from "./ConfigureABTestConditionSelection.vue";
import ConfigureConditionTimeOfDay from "./ConfigureConditionTimeOfDay.vue";
import ConfigureHierarchyCondition from "./ConfigureHierarchyCondition.vue";

const { t } = useI18n();
const store = useRuleEnginedStore();

const condition = defineModel<Condition>({ required: true });

defineProps<{
	index: number;
	expanded: boolean;
}>();

const emit = defineEmits<{ remove: []; toggle: [] }>();

const selectionComponentFor: Record<ConditionType, unknown> = {
	in_number: ConfigureConditionInSelection,
	in_string: ConfigureConditionInSelection,
	not_in_number: ConfigureConditionInSelection,
	not_in_string: ConfigureConditionInSelection,
	day_of_week: ConfigureConditionInSelection,
	comparison_string: ConfigureConditionComparisonSelection,
	comparison_number: ConfigureConditionComparisonSelection,
	comparison_date: ConfigureConditionComparisonSelection,
	comparison_boolean: ConfigureConditionComparisonSelection,
	between_date: ConfigureConditionBetweenSelection,
	between_number: ConfigureConditionBetweenSelection,
	ab_test: ConfigureABTestConditionSelection,
	time_of_day: ConfigureConditionTimeOfDay,
	in_hierarchy: ConfigureHierarchyCondition,
	not_in_hierarchy: ConfigureHierarchyCondition,
};

/** The hierarchy types are the only ones whose selection is neither a value nor a range. */
const isHierarchyType = (type: ConditionType | null): boolean =>
	type === "in_hierarchy" || type === "not_in_hierarchy";

/* Working state, seeded from the condition and written back on every change. */
const selectedDimension = ref<RuleDimension | null>(
	condition.value.dimension ?? null
);
const selectedType = ref<ConditionType | null>(
	isDraftCondition(condition.value) ? null : condition.value.type
);
const selectedOperator = ref<string>(
	"operator" in condition.value ? condition.value.operator : "equal_to"
);
const selectedValues = ref<(string | number)[]>(
	"values" in condition.value ? [...condition.value.values] : []
);
const selectedValue = ref<string | number | boolean>(
	"value" in condition.value && condition.value.type !== "time_of_day"
		? condition.value.value
		: ""
);
const selectedTimeRange = ref<TimeRangeValue>(
	condition.value.type === "time_of_day"
		? condition.value.value
		: { resolution: "hourly", times: [] }
);
const selectedFrom = ref<string | number>(
	"from" in condition.value ? condition.value.from : 0
);
const selectedTo = ref<string | number>(
	"to" in condition.value ? condition.value.to : 0
);
const selectedHierarchy = ref<HierarchySelection>(
	"excluded_branches" in condition.value
		? {
				hierarchy_definition_id: condition.value.hierarchy_definition_id,
				scope: condition.value.scope,
				selection_depth: condition.value.selection_depth,
				excluded_branches: [...condition.value.excluded_branches],
			}
		: emptyHierarchySelection()
);

const isInputValid = ref<boolean>(true);

const isMultiValueType = computed(() =>
	(MULTI_VALUE_CONDITION_TYPES as readonly string[]).includes(
		selectedType.value ?? ""
	)
);

const supportedTypes = computed(
	() => selectedDimension.value?.supported_condition_types ?? []
);

/** A dimension change can invalidate the chosen condition type. */
watch(selectedDimension, (dimension) => {
	if (!dimension) return;

	if (!dimension.supported_condition_types.includes(selectedType.value)) {
		selectedType.value = dimension.supported_condition_types[0] ?? null;
	}
});

const selection = computed({
	get() {
		if (isHierarchyType(selectedType.value)) return selectedHierarchy.value;
		if (isMultiValueType.value) return { selectedValues: selectedValues.value };
		if (selectedType.value === "time_of_day")
			return { timeRanges: selectedTimeRange.value };
		if (selectedType.value?.startsWith("comparison_")) {
			return {
				selectedOperator: selectedOperator.value,
				selectedValue: selectedValue.value,
			};
		}
		if (selectedType.value?.startsWith("between_")) {
			return { selectedFrom: selectedFrom.value, selectedTo: selectedTo.value };
		}
		return {};
	},
	set(value) {
		if (isHierarchyType(selectedType.value)) {
			selectedHierarchy.value = value as HierarchySelection;

			return;
		}
		if (isMultiValueType.value) selectedValues.value = value.selectedValues;
		if (selectedType.value === "time_of_day")
			selectedTimeRange.value = value.timeRanges;
		if (selectedType.value?.startsWith("comparison_")) {
			selectedOperator.value = value.selectedOperator;
			selectedValue.value = value.selectedValue;
		}
		if (selectedType.value?.startsWith("between_")) {
			selectedFrom.value = value.selectedFrom;
			selectedTo.value = value.selectedTo;
		}
	},
});

/**
 * Rebuilds the condition as the user edits. `parseCondition` needs a resolvable
 * dimension, so incomplete rows are simply not written back.
 */
watch(
	[
		selectedDimension,
		selectedType,
		selectedOperator,
		selectedValues,
		selectedValue,
		selectedTimeRange,
		selectedFrom,
		selectedTo,
		selectedHierarchy,
	],
	() => {
		if (!selectedDimension.value || !selectedType.value) return;

		condition.value = parseCondition({
			uuid: condition.value.uuid,
			dimension_uuid: selectedDimension.value.uuid,
			ab_test_uuid: selectedDimension.value.ab_test_uuid,
			type: selectedType.value,
			values: selectedValues.value,
			value:
				selectedType.value === "time_of_day"
					? selectedTimeRange.value
					: selectedValue.value,
			operator: selectedOperator.value,
			from: selectedFrom.value,
			to: selectedTo.value,
			...selectedHierarchy.value,
		});
	},
	{ deep: true }
);

const isComplete = computed(
	() => !!selectedDimension.value && !!selectedType.value && isInputValid.value
);

watch(
	() => condition.value.dimension,
	(dimension) => {
		if (dimension?.has_predefined_options) {
			store.getDimensionValues(dimension.uuid);
		}
	},
	{ immediate: true }
);

const summary = computed(() =>
	describeCondition(condition.value, t, {
		abTests: store.ruleEngineState.abTests,
		dimensionValues: store.dimensionValueCache,
	})
);

defineExpose({ isComplete });
</script>

<template>
	<div class="condition-row" :data-expanded="expanded">
		<div class="d-flex align-center summary-line" @click="emit('toggle')">
			<v-icon size="14" class="mr-2 chevron">
				{{ expanded ? "mdi-chevron-down" : "mdi-chevron-right" }}
			</v-icon>

			<rule-segments
				:connector="index === 0 ? t('rule_engine.if') : t('rule_engine.and')"
				:segments="summary"
			/>

			<v-icon v-if="!isComplete" size="16" color="warning" class="ml-2">
				mdi-alert-circle-outline
			</v-icon>

			<v-spacer />

			<v-btn
				icon
				variant="text"
				density="comfortable"
				color="error"
				v-tooltip="'Remove condition'"
				@click.stop="emit('remove')"
			>
				<v-icon size="18">fa: fa-light fa-trash-can</v-icon>
			</v-btn>
		</div>

		<v-expand-transition>
			<div v-if="expanded" class="editor pt-2">
				<div class="field-row">
					<label class="field-label">Dimension</label>
					<dimension-picker v-model="selectedDimension" />
				</div>

				<div class="field-row" v-if="!selectedDimension">
					<span></span>
					<div class="field-empty">
						Pick a dimension above — the condition and its value follow from
						what you choose.
					</div>
				</div>

				<div class="field-row" v-if="selectedDimension">
					<label class="field-label">Condition</label>
					<v-select
						v-model="selectedType"
						:items="supportedTypes"
						:item-title="
							(item) => t(`rule_engine.condition_types_selection.${item}`)
						"
						:item-value="(item) => item"
						variant="filled"
						density="compact"
						hide-details="auto"
					/>
				</div>

				<div class="field-row" v-if="selectedDimension && selectedType">
					<label class="field-label">Value</label>
					<div class="field-control">
						<component
							:is="selectionComponentFor[selectedType]"
							:type="selectedType"
							:selected-rule-dimension="selectedDimension"
							v-model="selection"
							v-model:is-input-valid="isInputValid"
						/>
					</div>
				</div>
			</div>
		</v-expand-transition>
	</div>
</template>

<style scoped lang="scss">
.condition-row {
	border-left: 5px solid #1abc9c;
	padding: 0.25rem 0 0.25rem 0.75rem;

	& + & {
		margin-top: 0.5rem;
	}
}

.summary-line {
	cursor: pointer;
	min-height: 40px;
}

.chevron {
	opacity: 0.6;
}

.field-row {
	display: grid;
	grid-template-columns: 7rem 1fr;
	align-items: start;
	gap: 0.75rem;
	margin-bottom: 0.75rem;
}

.field-label {
	padding-top: 0.75rem;
	font-size: 0.85rem;
	opacity: 0.75;
}

.field-control {
	min-width: 0;
}

.field-empty {
	padding-top: 0.35rem;
	font-size: 0.85rem;
	font-style: italic;
	opacity: 0.6;
}
</style>
